<template>
  <form
    :id="formId"
    class="inline-item-form"
    @submit.prevent="save"
  >
    <div class="inline-form-grid">
      <!-- Название -->
      <div class="inline-form-group inline-form-group--wide">
        <base-label :required="true">Название</base-label>
        <base-input
          v-model="form.title"
          placeholder="Название типа груза"
          :invalid="errors.title"
          @update:modelValue="handleTitleUpdate"
        />
        <span v-if="errors.title" class="error-message">
          Это поле обязательно
        </span>
      </div>

      <!-- Код -->
      <div class="inline-form-group inline-form-group--wide">
        <base-label :required="true">Код</base-label>
        <base-input
          v-model="form.code"
          placeholder="Уникальный код"
          :invalid="errors.code"
          @update:modelValue="handleCodeUpdate"
        />
        <span v-if="errors.code" class="error-message">
          Это поле обязательно
        </span>
      </div>

      <!-- Единица измерения -->
      <div class="inline-form-group">
        <base-label :required="true">Единица измерения</base-label>
        <CargoUnitSelect
          v-model="form.cargo_unit_id"
          placeholder="Выберите единицу измерения"
          :clearable="false"
          :invalid="errors.cargo_unit_id"
          :with-button="true"
          button-label="Создать единицу измерения"
          @change="errors.cargo_unit_id = false"
          @button-click="toggleInlineCargoUnitForm"
        />
        <span v-if="errors.cargo_unit_id" class="error-message">
          Это поле обязательно
        </span>
      </div>

      <!-- Форма создания единицы измерения -->
      <div
        v-if="showInlineCargoUnitForm"
        class="inline-form-group inline-form-group--wide"
      >
        <div class="inline-nested-form">
          <div class="inline-form-header">
            <span class="inline-form-title">
              Новая единица измерения
            </span>
            <button
              class="inline-form-close"
              type="button"
              @click="showInlineCargoUnitForm = false"
            >
              ✕
            </button>
          </div>

          <CargoUnitForm
            :is-loading="isCargoUnitCreating"
            submit-label="Создать и выбрать"
            @save="saveInlineCargoUnit"
            @cancel="showInlineCargoUnitForm = false"
          />
        </div>
      </div>

      <!-- Базовое количество -->
      <div class="inline-form-group">
        <base-label>Базовое количество</base-label>
        <base-input
          v-model="form.quantity"
          type="number"
          min="0"
          step="1"
          placeholder="0"
        />
      </div>

      <!-- Вес -->
      <div class="inline-form-group">
        <base-label>Вес (кг)</base-label>
        <base-input
          v-model="form.weight_kg"
          type="number"
          min="0"
          step="0.001"
          placeholder="0.000"
        />
      </div>

      <!-- Описание -->
      <div class="inline-form-group inline-form-group--wide">
        <base-label>Описание</base-label>
        <base-textarea
          v-model="form.description"
          placeholder="Описание типа груза"
        />
      </div>

      <!-- Блок габаритов / объёма -->
      <div
        class="inline-form-group inline-form-group--wide form-group-indent"
      >
        <div class="volume-header">
          <base-label>Размеры</base-label>
          <div class="volume-toggle">
            <button
              type="button"
              class="toggle-btn"
              :class="{
                'toggle-btn--active': dimensionMode === 'dimensions',
              }"
              @click="requestModeChange('dimensions')"
            >
              Габариты
            </button>
            <button
              type="button"
              class="toggle-btn"
              :class="{
                'toggle-btn--active': dimensionMode === 'volume',
              }"
              @click="requestModeChange('volume')"
            >
              Объём
            </button>
          </div>
        </div>

        <!-- Режим габаритов -->
        <div
          v-if="dimensionMode === 'dimensions'"
          class="dimensions-grid"
        >
          <div class="inline-form-group">
            <base-label>Длина, м</base-label>
            <base-input
              v-model="form.length_m"
              type="number"
              min="0"
              step="0.001"
              placeholder="0.000"
            />
          </div>
          <div class="inline-form-group">
            <base-label>Ширина, м</base-label>
            <base-input
              v-model="form.width_m"
              type="number"
              min="0"
              step="0.001"
              placeholder="0.000"
            />
          </div>
          <div class="inline-form-group">
            <base-label>Высота, м</base-label>
            <base-input
              v-model="form.height_m"
              type="number"
              min="0"
              step="0.001"
              placeholder="0.000"
            />
          </div>
          <div class="inline-form-group volume-calc">
            <base-label>Объём (расчётный)</base-label>
            <base-input
              :model-value="calculatedVolumeLabel"
              disabled
              :clearable="false"
            />
          </div>
        </div>

        <!-- Режим объёма -->
        <div v-else class="dimensions-grid">
          <div class="inline-form-group">
            <base-label>Объём (м³)</base-label>
            <base-input
              v-model="form.volume_m3"
              type="number"
              min="0"
              step="0.001"
              placeholder="0.000"
            />
          </div>
        </div>
      </div>
    </div>

    <div v-if="showActions" class="inline-form-actions">
      <base-primary-button
        type="submit"
        :loading="isLoading"
        class="btn-sm"
      >
        {{ submitLabel }}
      </base-primary-button>
      <base-button
        type="button"
        class="btn-sm"
        @click="$emit('cancel')"
      >
        Отмена
      </base-button>
    </div>

    <!-- Диалог подтверждения смены режима -->
    <base-dialog
      :visible="switchDialog.visible"
      title="Сменить режим ввода?"
      :message="switchDialog.message"
      :actions="[
        {
          key: 'confirm',
          label: 'Сменить и очистить',
          type: 'primary',
        },
        { key: 'close', label: 'Отмена', type: 'secondary' },
      ]"
      @action="handleSwitchDialogAction"
    />
  </form>
</template>

<script>
import { useNotificationStore } from "@/stores/notificationStore";
import { useCargoUnitStore } from "@/stores/cargoUnitStore";
import { useCargoItemRegistryStore } from "@/stores/cargoItemRegistryStore";
import { generateUniqueCode } from "@/utils/codeGenerator";
import { volumeM3FromMeterDimensions } from "@/utils/cargoCalculatorUtils";

import CargoUnitSelect from "@/components/Cargo/CargoUnitSelect.vue";
import CargoUnitForm from "@/components/Cargo/CargoUnitForm.vue";

export default {
  name: "CargoItemRegistryForm",

  components: {
    CargoUnitSelect,
    CargoUnitForm,
  },

  props: {
    formId: { type: String, default: null },
    initialData: { type: Object, default: null },
    isLoading: { type: Boolean, default: false },
    showActions: { type: Boolean, default: true },
    submitLabel: { type: String, default: "Сохранить" },
  },

  emits: ["save", "cancel"],

  data() {
    return {
      showInlineCargoUnitForm: false,
      isCargoUnitCreating: false,
      codeManuallyEdited: false,

      dimensionMode: "dimensions",

      switchDialog: {
        visible: false,
        targetMode: null,
        message: "",
      },

      form: {
        title: "",
        code: "",
        description: "",
        cargo_unit_id: null,
        quantity: "",
        weight_kg: "",
        height_m: "",
        width_m: "",
        length_m: "",
        volume_m3: "",
      },

      errors: {
        title: false,
        code: false,
        cargo_unit_id: false,
      },
    };
  },

  computed: {
    notificationStore() {
      return useNotificationStore();
    },

    registryItems() {
      return useCargoItemRegistryStore().items;
    },

    calculatedVolume() {
      return volumeM3FromMeterDimensions({
        heightM: this.form.height_m,
        lengthM: this.form.length_m,
        widthM: this.form.width_m,
      });
    },

    calculatedVolumeLabel() {
      return this.calculatedVolume === null
        ? ""
        : `${this.calculatedVolume} м³`;
    },

    hasDimensionsData() {
      return (
        (this.form.length_m !== "" &&
          this.form.length_m !== null) ||
        (this.form.width_m !== "" && this.form.width_m !== null) ||
        (this.form.height_m !== "" && this.form.height_m !== null)
      );
    },

    hasVolumeData() {
      return (
        this.form.volume_m3 !== "" && this.form.volume_m3 !== null
      );
    },
  },

  methods: {
    handleTitleUpdate(value) {
      this.errors.title = false;
      if (this.codeManuallyEdited) return;

      this.form.code = generateUniqueCode(
        value,
        this.registryItems,
        this.initialData?.id
      );
      this.errors.code = false;
    },

    handleCodeUpdate() {
      this.errors.code = false;
      this.codeManuallyEdited = true;
    },

    toggleInlineCargoUnitForm() {
      this.showInlineCargoUnitForm = !this.showInlineCargoUnitForm;
    },

    async saveInlineCargoUnit(formData) {
      this.isCargoUnitCreating = true;
      try {
        const store = useCargoUnitStore();
        const created = await store.createUnit(formData);

        this.form.cargo_unit_id = created.id;
        this.errors.cargo_unit_id = false;
        this.showInlineCargoUnitForm = false;

        this.notificationStore.showSuccess(
          "Единица измерения создана и выбрана"
        );
      } catch (error) {
        console.error(
          "Ошибка при создании единицы измерения:",
          error
        );
      } finally {
        this.isCargoUnitCreating = false;
      }
    },

    initForm() {
      if (!this.initialData) return;

      this.form = {
        title: this.initialData.title || "",
        code: this.initialData.code || "",
        description: this.initialData.description || "",
        cargo_unit_id: this.initialData.cargo_unit_id || null,
        quantity: this.initialData.quantity ?? "",
        weight_kg: this.initialData.weight_kg ?? "",
        height_m: this.initialData.height_m ?? "",
        width_m: this.initialData.width_m ?? "",
        length_m: this.initialData.length_m ?? "",
        volume_m3: this.initialData.volume_m3 ?? "",
      };
      this.codeManuallyEdited = Boolean(this.form.code);

      const hasDimensions =
        this.initialData.length_m ||
        this.initialData.width_m ||
        this.initialData.height_m;

      this.dimensionMode =
        this.initialData.volume_m3 && !hasDimensions
          ? "volume"
          : "dimensions";
    },

    requestModeChange(targetMode) {
      if (targetMode === this.dimensionMode) return;

      const hasData =
        targetMode === "volume"
          ? this.hasDimensionsData
          : this.hasVolumeData;

      if (!hasData) {
        this.applyModeChange(targetMode);
        return;
      }

      const fromLabel =
        targetMode === "volume" ? "габаритов" : "объёма";

      this.switchDialog = {
        visible: true,
        targetMode,
        message: `Заполненные данные ${fromLabel} будут очищены. Продолжить?`,
      };
    },

    handleSwitchDialogAction(key) {
      this.switchDialog.visible = false;
      if (key === "confirm") {
        this.applyModeChange(this.switchDialog.targetMode);
      }
      this.switchDialog.targetMode = null;
    },

    applyModeChange(mode) {
      this.dimensionMode = mode;
      if (mode === "volume") {
        this.form.height_m = "";
        this.form.width_m = "";
        this.form.length_m = "";
      } else {
        this.form.volume_m3 = "";
      }
    },

    validate() {
      let isValid = true;
      this.errors = {
        title: false,
        code: false,
        cargo_unit_id: false,
      };

      if (!String(this.form.title).trim()) {
        this.errors.title = true;
        isValid = false;
      }
      if (!String(this.form.code).trim()) {
        this.errors.code = true;
        isValid = false;
      }
      if (!this.form.cargo_unit_id) {
        this.errors.cargo_unit_id = true;
        isValid = false;
      }

      if (!isValid) {
        this.notificationStore.showError(
          "Пожалуйста, заполните все обязательные поля корректно"
        );
      }

      return isValid;
    },

    parseDecimal(value) {
      if (value === "" || value === null || value === undefined)
        return null;
      const parsed = parseFloat(value);
      return isNaN(parsed) ? null : parsed;
    },

    save() {
      if (!this.validate()) {
        this.$nextTick(() => {
          const firstErrorElement = document.querySelector(
            ".invalid, .error-message"
          );
          if (firstErrorElement) {
            const formGroup = firstErrorElement.closest(
              ".inline-form-group"
            );
            (formGroup || firstErrorElement).scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }
        });
        return;
      }

      const isDimensions = this.dimensionMode === "dimensions";

      this.$emit("save", {
        title: this.form.title,
        code: this.form.code,
        description: this.form.description || null,
        cargo_unit_id: this.form.cargo_unit_id || null,
        quantity:
          this.form.quantity !== ""
            ? parseInt(this.form.quantity)
            : null,
        weight_kg: this.parseDecimal(this.form.weight_kg),
        height_m: isDimensions
          ? this.parseDecimal(this.form.height_m)
          : null,
        width_m: isDimensions
          ? this.parseDecimal(this.form.width_m)
          : null,
        length_m: isDimensions
          ? this.parseDecimal(this.form.length_m)
          : null,
        volume_m3: isDimensions
          ? null
          : this.parseDecimal(this.form.volume_m3),
      });
    },
  },

  created() {
    this.initForm();
  },
};
</script>

<style scoped>
.inline-form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 10px;
  margin-bottom: var(--base-in-indent);
}

.inline-form-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.inline-form-group--wide {
  grid-column: 1 / -1;
}

.form-group-indent {
  margin-top: 6px;
}

.inline-nested-form {
  border-radius: 8px;
  padding: 12px;
  border: 2px dashed #b6b5b5;
  background: #eff6ff;
}

.inline-form-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.inline-form-title {
  font-weight: 600;
  font-size: 14px;
  color: #374151;
}

.inline-form-close {
  background: none;
  border: none;
  font-size: 14px;
  color: #9ca3af;
  cursor: pointer;
  padding: 0 4px;
  line-height: 1;
}

.inline-form-close:hover {
  color: #374151;
}

.volume-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.volume-toggle {
  display: flex;
  background: var(--color-grey);
  border-radius: 6px;
  padding: 3px;
  gap: 2px;
}

.toggle-btn {
  padding: 4px 14px;
  font-size: 13px;
  border-radius: 4px;
  border: none;
  background: transparent;
  color: #6b7280;
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease;
}

.toggle-btn--active {
  background: #fff;
  color: var(--color-dark);
  font-weight: 500;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.dimensions-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.inline-form-actions {
  display: flex;
  gap: 8px;
}

.btn-sm {
  padding: 6px 14px;
  font-size: 13px;
}

.error-message {
  margin-top: 5px;
  color: var(--color-danger);
  font-size: 12px;
}

@media (max-width: 900px) {
  .inline-form-grid {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 600px) {
  .inline-form-grid {
    grid-template-columns: 1fr;
  }

  .inline-form-group--wide {
    grid-column: span 1;
  }

  .dimensions-grid {
    grid-template-columns: 1fr;
  }
}
</style>
